import { ShieldCheck, Gem, Award } from "lucide-react";
import { fileUrl } from "@/lib/api";

const BRAND_PHOTO = "vvs-heritage/products/60d0be70-9cf0-4ee8-8e80-4f5363c1d331.jpeg";

const CONTENT = {
    "a-propos": {
        title: "À propos",
        body: (
            <>
                <p>VVS Héritage est née à Québec d'une conviction simple : l'éclat diamant ne devrait pas coûter un héritage.</p>
                <p className="mt-4">Nous concevons des bijoux en argent 925 sertis de moissanite VVS1 certifiée GRA — la pierre qui reproduit l'éclat du diamant, sans le prix, sans l'extraction minière.</p>
                <p className="mt-4">Chaque pièce est sertie à la main, contrôlée une par une, et expédiée avec son certificat GRA unique et vérifiable.</p>
                <p className="mt-4">Notre culture : le streetwear premium, le hip-hop, l'exigence du détail. Nos clients : ceux qui veulent briller sans se mentir.</p>
                <img
                    src={fileUrl(BRAND_PHOTO)}
                    alt="Chaîne moissanite VVS Héritage portée"
                    data-testid="about-brand-photo"
                    className="mt-8 w-full rounded-2xl border border-white/10 object-cover max-h-[480px]"
                />
                <p className="mt-3 text-xs text-slate-500 uppercase tracking-widest">Chaîne tennis moissanite VVS1 — pièce portée, studio VVS Héritage</p>
            </>
        ),
    },
    "authenticite": {
        title: "Authenticité & certificat GRA",
        body: (
            <>
                <p>Chaque moissanite VVS Héritage est certifiée par la <strong>Gemological Research Association (GRA)</strong> — l'un des laboratoires de certification les plus reconnus pour la moissanite.</p>
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="glass-card rounded-xl p-4"><ShieldCheck className="w-5 h-5 mb-2 text-[#D97706]" /><div className="text-sm font-medium">Certificat individuel</div><div className="text-xs text-slate-400 mt-1">Chaque pierre a son propre certificat numéroté.</div></div>
                    <div className="glass-card rounded-xl p-4"><Gem className="w-5 h-5 mb-2 text-[#D97706]" /><div className="text-sm font-medium">VVS1 · D-F</div><div className="text-xs text-slate-400 mt-1">Pureté maximale, blanc exceptionnel.</div></div>
                    <div className="glass-card rounded-xl p-4"><Award className="w-5 h-5 mb-2 text-[#D97706]" /><div className="text-sm font-medium">Vérifiable en ligne</div><div className="text-xs text-slate-400 mt-1">Numéro de série consultable sur le registre GRA.</div></div>
                </div>
                <p className="mt-6">La moissanite est une pierre de synthèse d'une dureté de 9.25 sur l'échelle de Mohs — juste derrière le diamant (10). Elle ne ternit pas, ne se raye pas au quotidien, et offre un feu supérieur au diamant.</p>
            </>
        ),
    },
    "retour": {
        title: "Politique de retour",
        body: (
            <>
                <p><strong>Délai :</strong> 14 jours à compter de la réception.</p>
                <p className="mt-4"><strong>Conditions :</strong> produit non porté, dans son emballage d'origine, avec certificat GRA intact.</p>
                <p className="mt-4"><strong>Frais de retour :</strong> à la charge du client, sauf défaut de fabrication ou erreur de notre part — dans ce cas, étiquette prépayée fournie.</p>
                <p className="mt-4"><strong>Remboursement :</strong> sous 5 à 10 jours ouvrables après réception et inspection, sur la méthode de paiement d'origine.</p>
                <p className="mt-4"><strong>Échanges :</strong> possibles pour une autre taille/variante selon disponibilité.</p>
                <p className="mt-4 text-slate-400">Pour initier un retour : contact@vvsheritage.ca avec votre numéro de commande.</p>
            </>
        ),
    },
    "cgv": {
        title: "Conditions générales de vente",
        body: (
            <>
                <p><strong>1. Objet.</strong> Les présentes conditions régissent la vente de bijoux VVS Héritage sur ce site.</p>
                <p className="mt-4"><strong>2. Prix.</strong> Tous les prix sont en dollars canadiens (CAD), TPS et TVQ en sus, calculées au checkout selon la province de livraison.</p>
                <p className="mt-4"><strong>3. Commande.</strong> La commande est ferme après confirmation du paiement via Stripe ou PayPal.</p>
                <p className="mt-4"><strong>4. Livraison.</strong> Postes Canada, livraison assurée. Forfait 15 $ CAD, offerte dès 500 $ CAD. Délais : 3 à 7 jours ouvrables au Québec.</p>
                <p className="mt-4"><strong>5. Rétractation.</strong> 14 jours, conformément à notre politique de retour.</p>
                <p className="mt-4"><strong>6. Responsabilité.</strong> VVS Héritage décline toute responsabilité en cas d'usage anormal des bijoux.</p>
                <p className="mt-4"><strong>7. Droit applicable.</strong> Province de Québec, Canada.</p>
            </>
        ),
    },
    "confidentialite": {
        title: "Politique de confidentialité",
        body: (
            <>
                <p><strong>Données collectées :</strong> nom, courriel, adresse de livraison, historique de commandes. Aucune donnée bancaire n'est stockée (Stripe / PayPal uniquement).</p>
                <p className="mt-4"><strong>Utilisation :</strong> traitement des commandes, suivi, service client, newsletter (avec consentement).</p>
                <p className="mt-4"><strong>Partage :</strong> uniquement avec nos prestataires (Postes Canada, Stripe, PayPal, Resend) pour l'exécution de votre commande.</p>
                <p className="mt-4"><strong>Vos droits :</strong> accès, rectification, suppression sur demande à contact@vvsheritage.ca.</p>
                <p className="mt-4"><strong>Cookies :</strong> cookies fonctionnels uniquement (panier, session admin).</p>
            </>
        ),
    },
};

export default function LegalPage({ page }) {
    const content = CONTENT[page] || CONTENT["a-propos"];
    return (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16" data-testid={`legal-page-${page}`}>
            <h1 className="font-serif-luxe text-3xl sm:text-5xl chrome-text font-semibold mb-10">{content.title}</h1>
            <div className="text-slate-300 leading-relaxed text-base">{content.body}</div>
        </div>
    );
}
